import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { fetchJson } from '@/components/inbox/utils'
import { stripHtml } from '@/lib/sanitizeHtml'
import type { Message } from '@/components/inbox/types'

const EDIT_WINDOW_MS = 10 * 60 * 1000

interface UseMessageEditorParams {
  conversationId: number
  messages: Message[]
  currentAgentId: number
}

export function useMessageEditor({ conversationId, messages, currentAgentId }: UseMessageEditorParams) {
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null)
  const [editingBody, setEditingBody] = useState('')
  const editingPlainTextRef = useRef('')
  const [hasEditingText, setHasEditingText] = useState(false)
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [deletingMessage, setDeletingMessage] = useState<{ conversationId: number; messageId: number } | null>(null)

  const canEditMessage = useCallback((message: Message): boolean => {
    if (message.deleted_at) return false
    if (message.author_type !== 'Agent') return false
    if (!('id' in message.author) || message.author.id !== currentAgentId) return false
    const createdAt = new Date(message.created_at).getTime()
    if (Number.isNaN(createdAt)) return false
    return Date.now() - createdAt <= EDIT_WINDOW_MS
  }, [currentAgentId])

  const startEditingMessage = useCallback((message: Message) => {
    const plainText = stripHtml(message.body || '')
    setEditingMessageId(message.id)
    setEditingBody(message.body || '')
    editingPlainTextRef.current = plainText
    setHasEditingText(plainText.trim().length > 0)
  }, [])

  const cancelEditingMessage = useCallback(() => {
    setEditingMessageId(null)
    setEditingBody('')
    editingPlainTextRef.current = ''
    setHasEditingText(false)
  }, [])

  const saveEditedMessage = useCallback(async () => {
    if (editingMessageId === null) return

    const messageToEdit = messages.find((msg) => msg.id === editingMessageId)
    const hasAttachments = (messageToEdit?.attachments?.length || 0) > 0
    const hasBody = editingPlainTextRef.current.trim().length > 0

    if (!hasBody && !hasAttachments) {
      toast.error('A mensagem não pode ficar vazia')
      return
    }

    setIsSavingEdit(true)
    try {
      const response = await fetchJson(`/conversations/${conversationId}/messages/${editingMessageId}`, {
        method: 'PATCH',
        body: { message: { body: editingBody } },
      })

      if (!response.ok) {
        const data = await response.json().catch(() => null)
        throw new Error(data?.error || 'Erro ao editar mensagem')
      }

      cancelEditingMessage()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao editar mensagem')
    } finally {
      setIsSavingEdit(false)
    }
  }, [conversationId, editingMessageId, editingBody, messages, cancelEditingMessage])

  const handleEditingTextChange = useCallback((text: string) => {
    editingPlainTextRef.current = text
    const nextHasText = text.trim().length > 0
    setHasEditingText((prev) => (prev === nextHasText ? prev : nextHasText))
  }, [])

  return {
    editingMessageId,
    editingBody,
    setEditingBody,
    hasEditingText,
    isSavingEdit,
    deletingMessage,
    setDeletingMessage,
    canEditMessage,
    startEditingMessage,
    cancelEditingMessage,
    saveEditedMessage,
    handleEditingTextChange,
  }
}
